import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Typography,
  Space,
  Button,
  Table,
  Spin,
  Alert,
  DatePicker,
  Tag,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FileTextOutlined,
  BankOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  AccountBookOutlined,
  BarChartOutlined,
  ReconciliationOutlined,
  PlusOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExceptionOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { poMilestoneService } from '@/services/poMilestone.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  getFinanceDashboardActionSummary,
  emptyFinanceDashboardSummary,
  type FinanceDashboardActionSummary,
  type PendingApprovalPaymentVoucherRow,
  type PendingApprovalJournalVoucherRow,
  type PendingApprovalGrnRow,
  type CustomerAdvanceReadyRow,
  type PoReadyForAdvanceRequestRow,
  type SarReadyForPaymentRow,
  type SalesInvoiceReadyRow,
  type SupplierInvoiceReadyRow,
  type SupplierAdvanceRecoveryQueueRow,
} from '@/services/financeDashboard.service';
import { recoveryStatusTagColor } from '@/services/supplierAdvanceRefund.service';
import { FinanceSectionCard } from '@/components/finance/control-tower/FinanceSectionCard';
import { FinanceMetricTile } from '@/components/finance/control-tower/FinanceMetricTile';
import { FinanceMetricStrip } from '@/components/finance/control-tower/FinanceMetricStrip';
import { FinanceSubQueuePanel } from '@/components/finance/control-tower/FinanceSubQueuePanel';
import { FinanceAttentionBrief } from '@/components/finance/control-tower/FinanceAttentionBrief';
import { FinanceOutlookRibbon } from '@/components/finance/control-tower/FinanceOutlookRibbon';
import { FinanceOverdueFollowUpZone } from '@/components/finance/control-tower/FinanceOverdueFollowUpZone';

const { Title, Text, Paragraph } = Typography;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const FinanceDashboard: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<FinanceDashboardActionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOnDate, setAsOnDate] = useState<string | undefined>(undefined);

  const { data: milestoneSummary } = useQuery({
    queryKey: ['po-milestone-dashboard-summary'],
    queryFn: () => poMilestoneService.dashboardSummary(),
  });

  const fetchSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    getFinanceDashboardActionSummary(asOnDate ? { as_on_date: asOnDate } : undefined)
      .then(setSummary)
      .catch((e: any) => setError(e?.message ?? t('failedLoadDashboard')))
      .finally(() => setLoading(false));
  }, [asOnDate, t]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const s = summary ?? emptyFinanceDashboardSummary();

  const supplierAdvanceReadyCount =
    (s.po_ready_for_sar_count ?? 0) + (s.sar_ready_for_payment_count ?? 0);
  const pendingApprovalTotal =
    (s.pending_approval_pv_count ?? 0) +
    (s.pending_approval_jv_count ?? 0) +
    (s.pending_approval_grn_count ?? 0);

  const recovery = s.supplier_advance_recovery ?? {
    pending_count: 0,
    partial_count: 0,
    overdue_count: 0,
    exposure_total: 0,
    queue_rows: [],
  };
  const recoveryQueueCount = recovery.queue_rows?.length ?? 0;

  const recoveryQueueColumns: ColumnsType<SupplierAdvanceRecoveryQueueRow> = [
    { title: t('supplier'), dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
    { title: t('po'), dataIndex: 'po_number', key: 'po_number', width: 110 },
    { title: t('controlTower.colSar'), dataIndex: 'advance_request_number', key: 'advance_request_number', width: 110 },
    {
      title: t('balance'),
      dataIndex: 'remaining_refundable_balance',
      key: 'remaining_refundable_balance',
      align: 'right',
      width: 110,
      render: (v: number) => formatCurrency(v ?? 0),
    },
    {
      title: t('controlTower.ageDays'),
      dataIndex: 'exposure_age_days',
      key: 'exposure_age_days',
      width: 90,
      align: 'center' as const,
    },
    {
      title: t('status'),
      dataIndex: 'recovery_status',
      key: 'recovery_status',
      width: 140,
      render: (status: string) => <Tag color={recoveryStatusTagColor(status)}>{status}</Tag>,
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_: unknown, r: SupplierAdvanceRecoveryQueueRow) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          {t('controlTower.recover')}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: token.colorBgContainer,
            paddingBottom: 8,
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              {t('controlTower.title')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              {t('controlTower.subtitle')}
            </Paragraph>
            <Space wrap size={[4, 4]}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('attention-brief')}>
                {t('controlTower.navAttention')}
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('collections-payables')}>
                {t('controlTower.navCollections')}
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('document-preparation')}>
                {t('controlTower.navDocumentPrep')}
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('pending-approvals')}>
                {t('controlTower.navApprovals')}
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('month-end')}>
                {t('controlTower.navMonthEnd')}
              </Button>
            </Space>
          </div>
          <Space wrap>
            <Space>
              <Text type="secondary">{t('asOnDate')}:</Text>
              <DatePicker
                value={asOnDate ? dayjs(asOnDate) : dayjs()}
                onChange={(d) => setAsOnDate(d ? d.format('YYYY-MM-DD') : undefined)}
                allowClear
              />
            </Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
              {tc('refresh')}
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        <FinanceAttentionBrief summary={s} formatCurrency={formatCurrency} />
        <FinanceOutlookRibbon summary={s} formatCurrency={formatCurrency} />

        <FinanceSectionCard zone="actions" title={t('controlTower.quickActions')}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/receipt-vouchers/new')}>
              {t('controlTower.newReceipt')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/payment-vouchers/new')}>
              {t('controlTower.newPayment')}
            </Button>
            <Button icon={<BarChartOutlined />} onClick={() => navigate('/fin/reports/customer-outstanding')}>
              {t('customerOutstanding')}
            </Button>
            <Button icon={<BarChartOutlined />} onClick={() => navigate('/fin/reports/supplier-outstanding')}>
              {t('supplierOutstanding')}
            </Button>
            <Button icon={<ReconciliationOutlined />} onClick={() => navigate('/fin/reports/settlement-register')}>
              {t('settlementRegister')}
            </Button>
            <Button icon={<AccountBookOutlined />} onClick={() => navigate('/fin/reports/ledger')}>
              {t('controlTower.ledgerShort')}
            </Button>
          </Space>
        </FinanceSectionCard>

        <FinanceOverdueFollowUpZone
          summary={s}
          asOnDate={asOnDate}
          formatCurrency={formatCurrency}
        />

        <FinanceSectionCard
          zone="documentPrep"
          sectionId="document-preparation"
          title={
            <Space>
              <FileTextOutlined />
              <span>{t('controlTower.documentPreparation')}</span>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <FinanceSubQueuePanel
              zone="docPrepCustomerAdvance"
              title={t('controlTower.customerAdvanceInvoicesToPrepare')}
              count={s.customer_advance_ready_count ?? 0}
            >
              {!(s.customer_advance_ready?.length) ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {t('controlTower.noneReady')}
                </Paragraph>
              ) : (
                <Table
                  rowKey="sales_order_id"
                  size="small"
                  pagination={false}
                  dataSource={s.customer_advance_ready}
                  columns={[
                    { title: t('controlTower.colSo'), dataIndex: 'sales_order_number', key: 'sales_order_number', width: 120 },
                    { title: t('customer'), dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
                    {
                      title: t('remaining'),
                      dataIndex: 'remaining_advance',
                      key: 'remaining_advance',
                      align: 'right' as const,
                      width: 110,
                      render: (v: number) => formatCurrency(v ?? 0),
                    },
                    {
                      title: '',
                      key: 'action',
                      width: 140,
                      render: (_: unknown, r: CustomerAdvanceReadyRow) => (
                        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
                          {t('controlTower.openSo')}
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </FinanceSubQueuePanel>
            <FinanceSubQueuePanel
              zone="docPrepSupplierAdvance"
              title={t('controlTower.supplierAdvanceCreateRelease')}
              count={supplierAdvanceReadyCount}
            >
              {supplierAdvanceReadyCount === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {t('controlTower.noneReady')}
                </Paragraph>
              ) : (
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Text type="secondary">{t('controlTower.posNeedingSar')}</Text>
                    <Table
                      rowKey="purchase_order_id"
                      size="small"
                      pagination={false}
                      locale={{ emptyText: t('none') }}
                      style={{ marginTop: 8 }}
                      dataSource={s.supplier_advance_ready?.po_ready_for_advance_request ?? []}
                      columns={[
                        { title: t('po'), dataIndex: 'po_number', key: 'po_number', width: 110 },
                        { title: t('supplier'), dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                        {
                          title: '',
                          key: 'action',
                          width: 90,
                          render: (_: unknown, r: PoReadyForAdvanceRequestRow) => (
                            <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
                              {t('controlTower.openPo')}
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Text type="secondary">{t('controlTower.sarsNeedingPayment')}</Text>
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      locale={{ emptyText: t('none') }}
                      style={{ marginTop: 8 }}
                      dataSource={s.supplier_advance_ready?.sar_ready_for_payment ?? []}
                      columns={[
                        { title: t('controlTower.colSar'), dataIndex: 'advance_request_number', key: 'advance_request_number', width: 110 },
                        { title: t('supplier'), dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                        { title: t('po'), dataIndex: 'po_number', key: 'po_number', width: 100 },
                        {
                          title: t('balance'),
                          dataIndex: 'balance_to_pay',
                          key: 'balance_to_pay',
                          align: 'right' as const,
                          width: 100,
                          render: (v: number) => formatCurrency(v ?? 0),
                        },
                        {
                          title: '',
                          key: 'action',
                          width: 90,
                          render: (_: unknown, r: SarReadyForPaymentRow) => (
                            <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
                              {t('controlTower.pay')}
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>
              )}
            </FinanceSubQueuePanel>
            <FinanceSubQueuePanel
              zone="docPrepSupplierAdvance"
              title={t('controlTower.supplierAdvanceRecoveryCancelled')}
              count={recovery.pending_count + recovery.partial_count}
            >
              <FinanceMetricStrip zone="metricsPayables" title={t('controlTower.supplierAdvanceRecovery')}>
                <Col xs={24} sm={12} md={6}>
                  <FinanceMetricTile
                    title={t('controlTower.recoveryPending')}
                    count={recovery.pending_count}
                    urgency={recovery.pending_count > 0 ? 'critical' : 'neutral'}
                    footerLink={{
                      label: t('controlTower.viewRegister'),
                      onClick: () => navigate('/finance/ap/supplier-advance-refunds'),
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <FinanceMetricTile
                    title={t('controlTower.partialRefund')}
                    count={recovery.partial_count}
                    urgency={recovery.partial_count > 0 ? 'attention' : 'neutral'}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <FinanceMetricTile
                    title={t('controlTower.overdueRecovery')}
                    count={recovery.overdue_count}
                    urgency={recovery.overdue_count > 0 ? 'critical' : 'neutral'}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <FinanceMetricTile
                    title={t('controlTower.recoveryExposure')}
                    count={0}
                    valueDisplay={formatCurrency(recovery.exposure_total ?? 0)}
                    urgency={(recovery.exposure_total ?? 0) > 0 ? 'attention' : 'neutral'}
                  />
                </Col>
              </FinanceMetricStrip>
              {recoveryQueueCount === 0 ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {t('controlTower.noOpenRecoveryExposures')}
                </Paragraph>
              ) : (
                <Table
                  rowKey="exposure_id"
                  size="small"
                  pagination={false}
                  dataSource={recovery.queue_rows}
                  columns={recoveryQueueColumns}
                />
              )}
            </FinanceSubQueuePanel>
            <FinanceSubQueuePanel
              zone="docPrepSalesInvoice"
              title={t('controlTower.taxInvoicesToCreate')}
              count={s.sales_invoices_ready_count ?? 0}
            >
              {!(s.sales_invoices_ready?.length) ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {t('controlTower.noneReady')}
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.sales_invoices_ready}
                  columns={[
                    { title: t('controlTower.colDn'), dataIndex: 'delivery_note_number', key: 'delivery_note_number', width: 120 },
                    { title: t('customer'), dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
                    { title: t('controlTower.delivered'), dataIndex: 'delivery_date', key: 'delivery_date', width: 100 },
                    { title: t('controlTower.colSo'), dataIndex: 'sales_order_number', key: 'sales_order_number', width: 110 },
                    {
                      title: '',
                      key: 'action',
                      width: 100,
                      render: (_: unknown, r: SalesInvoiceReadyRow) => (
                        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
                          {t('controlTower.openDn')}
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </FinanceSubQueuePanel>
            <FinanceSubQueuePanel
              zone="docPrepSupplierBill"
              title={t('controlTower.supplierBillsToCreate')}
              count={s.supplier_invoices_ready_count ?? 0}
            >
              {!(s.supplier_invoices_ready?.length) ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {t('controlTower.noneReady')}
                </Paragraph>
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={s.supplier_invoices_ready}
                  columns={[
                    { title: t('controlTower.colGrn'), dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                    { title: t('date'), dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                    { title: t('supplier'), dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                    { title: t('po'), dataIndex: 'po_number', key: 'po_number', width: 110 },
                    {
                      title: '',
                      key: 'action',
                      width: 120,
                      render: (_: unknown, r: SupplierInvoiceReadyRow) => (
                        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
                          {t('controlTower.createBill')}
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </FinanceSubQueuePanel>
          </Space>
        </FinanceSectionCard>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <FinanceMetricStrip zone="metricsPayables" title={t('controlTower.poMilestonePayments')}>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.milestonesDue')}
                count={milestoneSummary?.dueCount ?? 0}
                icon={<ClockCircleOutlined />}
                urgency={(milestoneSummary?.dueCount ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: t('controlTower.register'),
                  onClick: () => navigate('/scm/procurement/reports/po-milestones'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.invoicesPending')}
                count={milestoneSummary?.pendingInvoices ?? 0}
                icon={<FileTextOutlined />}
                urgency={(milestoneSummary?.pendingInvoices ?? 0) > 0 ? 'attention' : 'neutral'}
              />
            </Col>
          </FinanceMetricStrip>

          <FinanceMetricStrip zone="metricsDrafts" title={t('controlTower.draftsAndVouchers')}>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.draftReceipts')}
                count={s.draft_receipt_vouchers_count}
                icon={<FileTextOutlined />}
                urgency={s.draft_receipt_vouchers_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_receipt_vouchers_count > 0
                    ? { label: t('open'), onClick: () => navigate('/fin/receipt-vouchers?status=Draft') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.draftPayments')}
                count={s.draft_payment_vouchers_count}
                icon={<BankOutlined />}
                urgency={s.draft_payment_vouchers_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_payment_vouchers_count > 0
                    ? { label: t('open'), onClick: () => navigate('/fin/payment-vouchers?status=Draft') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.draftBillPayments')}
                count={s.pending_supplier_bill_payment_pv_count ?? 0}
                icon={<BankOutlined />}
                urgency={(s.pending_supplier_bill_payment_pv_count ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={
                  (s.pending_supplier_bill_payment_pv_count ?? 0) > 0
                    ? {
                        label: t('open'),
                        onClick: () => navigate('/fin/payment-vouchers?status=Draft&payment_type=SupplierBillPayment'),
                      }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.draftJournals')}
                count={s.draft_journal_vouchers_count}
                icon={<AccountBookOutlined />}
                urgency={s.draft_journal_vouchers_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.draft_journal_vouchers_count > 0
                    ? { label: t('open'), onClick: () => navigate('/fin/gl/journals') }
                    : undefined
                }
              />
            </Col>
          </FinanceMetricStrip>

          <FinanceMetricStrip zone="metricsPayables" title={t('controlTower.settlementAndAdvances')}>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.openArSources')}
                count={s.open_ar_sources_count}
                icon={<SwapOutlined />}
                urgency={s.open_ar_sources_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.open_ar_sources_count > 0
                    ? { label: t('controlTower.settle'), onClick: () => navigate('/fin/reports/settlement-register') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.openApSources')}
                count={s.open_ap_sources_count}
                icon={<SwapOutlined />}
                urgency={s.open_ap_sources_count > 0 ? 'attention' : 'neutral'}
                footerLink={
                  s.open_ap_sources_count > 0
                    ? { label: t('controlTower.settle'), onClick: () => navigate('/fin/reports/settlement-register') }
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={8}>
              <FinanceMetricTile
                title={t('controlTower.pendingAdvanceRequests')}
                count={s.pending_supplier_advance_requests_count ?? 0}
                urgency={(s.pending_supplier_advance_requests_count ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: tc('view'), onClick: () => navigate('/scm/procurement/supplier-advance-requests') }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <FinanceMetricTile
                title={t('controlTower.advancesAwaitingAdjustment')}
                count={s.advances_awaiting_adjustment_count ?? 0}
                urgency={(s.advances_awaiting_adjustment_count ?? 0) > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={24} sm={8}>
              <FinanceMetricTile
                title={t('controlTower.paymentCommitmentsSar')}
                count={s.payment_commitments_count ?? 0}
                urgency={(s.payment_commitments_count ?? 0) > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <FinanceMetricTile
                title={t('controlTower.supplierExposureApAdvances')}
                count={0}
                valueDisplay={formatCurrency(s.supplier_advance_exposure ?? 0)}
              />
            </Col>
          </FinanceMetricStrip>

          <FinanceMetricStrip zone="metricsDocPrep" title={t('controlTower.documentPreparationCounts')}>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.customerAdvancesReady')}
                count={s.customer_advance_ready_count ?? 0}
                icon={<FileTextOutlined />}
                urgency={(s.customer_advance_ready_count ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: t('controlTower.viewQueue'), onClick: () => scrollToSection('document-preparation') }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.supplierAdvancesReady')}
                count={supplierAdvanceReadyCount}
                icon={<BankOutlined />}
                urgency={supplierAdvanceReadyCount > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: t('controlTower.viewQueue'), onClick: () => scrollToSection('document-preparation') }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.taxInvoicesReady')}
                count={s.sales_invoices_ready_count ?? 0}
                icon={<FileTextOutlined />}
                urgency={(s.sales_invoices_ready_count ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: t('controlTower.viewQueue'), onClick: () => scrollToSection('document-preparation') }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FinanceMetricTile
                title={t('controlTower.supplierBillsReady')}
                count={s.supplier_invoices_ready_count ?? 0}
                icon={<FileTextOutlined />}
                urgency={(s.supplier_invoices_ready_count ?? 0) > 0 ? 'attention' : 'neutral'}
                footerLink={{ label: t('controlTower.viewQueue'), onClick: () => scrollToSection('document-preparation') }}
              />
            </Col>
          </FinanceMetricStrip>
        </Space>

        <FinanceSectionCard
          zone="pendingApprovals"
          sectionId="pending-approvals"
          title={
            <Space>
              <ExceptionOutlined />
              <span>{t('controlTower.pendingApprovalsCount', { count: pendingApprovalTotal })}</span>
            </Space>
          }
        >
          {pendingApprovalTotal === 0 ? (
            <Text type="secondary">{t('controlTower.noPendingFinanceApprovals')}</Text>
          ) : (
            <Row gutter={[16, 16]}>
              {s.pending_approval_payment_vouchers.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>
                    {t('controlTower.paymentVouchersCount', {
                      count: s.pending_approval_pv_count ?? s.pending_approval_payment_vouchers.length,
                    })}
                  </Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={s.pending_approval_payment_vouchers}
                    columns={[
                      { title: t('voucher'), dataIndex: 'voucher_number', key: 'voucher_number', width: 120 },
                      { title: t('date'), dataIndex: 'voucher_date', key: 'voucher_date', width: 100 },
                      {
                        title: t('amount'),
                        dataIndex: 'amount',
                        key: 'amount',
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(v ?? 0),
                      },
                      { title: t('status'), dataIndex: 'status', key: 'status', width: 90 },
                      {
                        title: '',
                        key: 'action',
                        width: 70,
                        render: (_: unknown, r: PendingApprovalPaymentVoucherRow) => (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(`/fin/payment-vouchers/${r.id}`)}
                          >
                            {tc('view')}
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Col>
              )}
              {s.pending_approval_journal_vouchers.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>
                    {t('controlTower.journalVouchersCount', {
                      count: s.pending_approval_jv_count ?? s.pending_approval_journal_vouchers.length,
                    })}
                  </Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={s.pending_approval_journal_vouchers}
                    columns={[
                      { title: t('voucher'), dataIndex: 'voucher_number', key: 'voucher_number', width: 120 },
                      { title: t('date'), dataIndex: 'voucher_date', key: 'voucher_date', width: 100 },
                      {
                        title: t('amount'),
                        dataIndex: 'total_debit',
                        key: 'total_debit',
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(v ?? 0),
                      },
                      { title: t('status'), dataIndex: 'status', key: 'status', width: 90 },
                      {
                        title: '',
                        key: 'action',
                        width: 70,
                        render: (_: unknown, r: PendingApprovalJournalVoucherRow) => (
                          <Button type="link" size="small" onClick={() => navigate('/fin/gl/journals')}>
                            {tc('view')}
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Col>
              )}
              {s.pending_approval_grns.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>
                    {t('controlTower.grnsCount', {
                      count: s.pending_approval_grn_count ?? s.pending_approval_grns.length,
                    })}
                  </Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={s.pending_approval_grns}
                    columns={[
                      { title: t('controlTower.colGrn'), dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                      { title: t('date'), dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                      { title: t('status'), dataIndex: 'grn_status', key: 'grn_status', width: 90 },
                      {
                        title: '',
                        key: 'action',
                        width: 70,
                        render: (_: unknown, r: PendingApprovalGrnRow) => (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(`/scm/procurement/grn/${r.id}`)}
                          >
                            {tc('view')}
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Col>
              )}
            </Row>
          )}
        </FinanceSectionCard>

        <FinanceSectionCard
          zone="monthEnd"
          sectionId="month-end"
          title={
            <Space>
              <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              <span>{t('controlTower.monthEndReadiness')}</span>
            </Space>
          }
        >
          <Space direction="vertical" size="small">
            <div>
              <Text>{t('controlTower.draftReceiptsCount', { count: s.month_end.draft_receipts })}</Text>
              {s.month_end.draft_receipts > 0 && (
                <Button type="link" size="small" onClick={() => navigate('/fin/receipt-vouchers?status=Draft')}>
                  {t('open')}
                </Button>
              )}
            </div>
            <div>
              <Text>{t('controlTower.draftPaymentsCount', { count: s.month_end.draft_payments })}</Text>
              {s.month_end.draft_payments > 0 && (
                <Button type="link" size="small" onClick={() => navigate('/fin/payment-vouchers?status=Draft')}>
                  {t('open')}
                </Button>
              )}
            </div>
            <div>
              <Text>
                {t('controlTower.draftUnpostedJournals', {
                  draft: s.month_end.draft_journals,
                  unposted: s.month_end.unposted_journals,
                })}
              </Text>
              {(s.month_end.draft_journals > 0 || s.month_end.unposted_journals > 0) && (
                <Button type="link" size="small" onClick={() => navigate('/fin/gl/journals')}>
                  {t('open')}
                </Button>
              )}
            </div>
            <div>
              <Text>{t('controlTower.openArSourcesCount', { count: s.month_end.open_ar_sources })}</Text>
              {s.month_end.open_ar_sources > 0 && (
                <Button type="link" size="small" onClick={() => navigate('/fin/reports/settlement-register')}>
                  {t('controlTower.settle')}
                </Button>
              )}
            </div>
            <div>
              <Text>{t('controlTower.openApSourcesCount', { count: s.month_end.open_ap_sources })}</Text>
              {s.month_end.open_ap_sources > 0 && (
                <Button type="link" size="small" onClick={() => navigate('/fin/reports/settlement-register')}>
                  {t('controlTower.settle')}
                </Button>
              )}
            </div>
          </Space>
        </FinanceSectionCard>
      </Space>
    </div>
  );
};
